import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'react-aria-components/Button';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  notificationCountGateway,
  type NotificationCountGateway,
} from '../../features/notifications/notification-count-gateway';
import { useSession } from '../../features/session/SessionContext';
import { reportClientError } from '../../services/appwrite/report-client-error';
import { useAnnouncer } from '../accessibility/Announcer';
import { AccessibilityToolbar } from '../accessibility/AccessibilityToolbar';
import { AccessibleMenu, type AccessibleMenuHandle, type AccessibleMenuItem } from '../navigation/AccessibleMenu';

interface MenuLink {
  label: string;
  href: string;
  current?: boolean;
}

interface SiteHeaderProps {
  /** Gateway alternativo para pruebas; `null` omite esta consulta global. */
  notificationGateway?: NotificationCountGateway | null;
}

/**
 * Todas rutas internas, aunque una todavía no exista: mejor un «no
 * encontrado» propio que mandar a alguien al sitio Jekyll que estamos
 * dejando atrás. Se abren con `<Link>` porque `href` empieza con `/`.
 */
const publicLinks: MenuLink[] = [
  { label: 'Actividades', href: '/activities' },
  { label: 'Recursos', href: '/resources' },
  { label: 'Contacto', href: '/contact' },
];

export function SiteHeader({ notificationGateway: injectedGateway }: SiteHeaderProps) {
  const { state, signOut } = useSession();
  const { announce } = useAnnouncer();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const accessibleMenuRef = useRef<AccessibleMenuHandle>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const originRef = useRef<HTMLElement | null>(null);

  const authenticated = state.status === 'authenticated';
  const countGateway = injectedGateway === undefined
    ? (import.meta.env.MODE === 'test' ? null : notificationCountGateway)
    : injectedGateway;
  const notificationCount = useQuery({
    queryKey: ['notification-count'],
    queryFn: () => countGateway?.count() ?? Promise.resolve(0),
    enabled: authenticated && countGateway !== null,
  });
  const notificationLabel = notificationCount.data
    ? `Notificaciones (${notificationCount.data})`
    : 'Notificaciones';
  // El panel personal, las actividades que administro y las notificaciones
  // se mudaron a panel.rolsensorial.com: ninguna superficie de este repo
  // tiene panel propio, así que "Mi panel"/"Mi cuenta" y "Notificaciones"
  // siempre cruzan de dominio hacia ahí.
  const sessionLinks: MenuLink[] = authenticated
    ? [
        {
          label: 'Mi panel',
          href: 'https://panel.rolsensorial.com/',
        },
        {
          label: notificationLabel,
          href: 'https://panel.rolsensorial.com/notifications',
        },
      ]
    : [
        {
          label: 'Mi cuenta',
          href: 'https://panel.rolsensorial.com/',
        },
      ];

  const openMenu = useCallback(() => {
    setMenuOpen(true);
    announce('Menú desplegado.');
    window.setTimeout(() => accessibleMenuRef.current?.focusActive(), 0);
  }, [announce]);

  const enterMenu = useCallback(() => {
    originRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    if (window.matchMedia('(min-width: 768px)').matches) {
      announce('Menú.');
      accessibleMenuRef.current?.focusActive();
      return;
    }
    openMenu();
  }, [announce, openMenu]);

  const closeMenu = useCallback((restoreFocus = true) => {
    setMenuOpen(false);
    announce('Menú plegado.');
    if (restoreFocus) window.setTimeout(() => toggleRef.current?.focus(), 0);
  }, [announce]);

  const exitMenu = useCallback(() => {
    if (!window.matchMedia('(min-width: 768px)').matches) {
      closeMenu();
      return;
    }
    announce('Saliste del menú.');
    const destination = originRef.current;
    originRef.current = null;
    if (destination && document.contains(destination) && !menuRef.current?.contains(destination)) destination.focus();
    else document.getElementById('main-content')?.focus();
  }, [announce, closeMenu]);

  useEffect(() => {
    const onShortcut = (event: KeyboardEvent) => {
      if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || event.key.toLowerCase() !== 'm') return;
      event.preventDefault();
      if (menuOpen || menuRef.current?.contains(document.activeElement)) exitMenu();
      else enterMenu();
    };
    document.addEventListener('keydown', onShortcut);
    return () => document.removeEventListener('keydown', onShortcut);
  }, [enterMenu, exitMenu, menuOpen]);

  const leave = async () => {
    setSigningOut(true);
    try {
      await signOut();
      announce('Sesión cerrada.');
      closeMenu(false);
    } catch (error) {
      console.error('[Rol Sensorial] No se pudo cerrar la sesión.', error);
      reportClientError({
        source: 'web-app',
        operation: 'sign-out',
        message: 'No se pudo cerrar la sesión.',
        details: String(error),
      });
      announce('No pudimos cerrar la sesión. Inténtalo de nuevo.');
    } finally {
      setSigningOut(false);
    }
  };

  const items: AccessibleMenuItem[] = [
    ...publicLinks.map((link) => ({ ...link, key: `public:${link.href}` })),
    ...sessionLinks.map((link) => ({ ...link, key: `session:${link.href}` })),
    ...(authenticated ? [{ key: 'sign-out', label: signingOut ? 'Cerrando sesión…' : 'Cerrar sesión', disabled: signingOut, onSelect: () => void leave() }] : []),
  ];

  return (
    <header className="site-header">
      <nav className="navbar navbar-expand-md" aria-label="Principal">
        <div className="container">
          <Link className="navbar-brand brand-mark brand-mark-solo" to="/" title="Logo Rol Sensorial">
            <img className="brand-mark-icon" src="/assets/logo/logo.svg" alt="Logo Rol Sensorial" width="48" height="48" />
          </Link>

          <div className="d-flex align-items-center gap-2 order-md-3">
            <AccessibilityToolbar />
            <Button
              ref={toggleRef}
              className="navbar-toggler"
              aria-controls="navbarPrincipal"
              aria-expanded={menuOpen}
              aria-label={menuOpen ? 'Cerrar menú de navegación' : 'Abrir menú de navegación'}
              aria-haspopup="menu"
              aria-keyshortcuts="Alt+M"
              onPress={() => (menuOpen ? closeMenu() : openMenu())}
            >
              <span className="navbar-toggler-icon" />
            </Button>
          </div>

          <div
            ref={menuRef}
            id="navbarPrincipal"
            className={`collapse navbar-collapse${menuOpen ? ' show' : ''}`}
            aria-keyshortcuts="Alt+M"
          >
            <AccessibleMenu ref={accessibleMenuRef} label="Principal" items={items} className="navbar-nav ms-auto" itemClassName="nav-item" onEscape={exitMenu} onSelect={() => setMenuOpen(false)} />
          </div>
        </div>
      </nav>
    </header>
  );
}
